import type { AiMarkerKind } from "./markers";

export const DEFAULT_DISCLAIMER_VERSION = "v1";

/**
 * Disclaimer copy, keyed by version and then by marker kind.
 *
 * The wording is a placeholder — legal has not delivered final text. The
 * `PENDING_LEGAL_` prefix is deliberately greppable across every consuming repo so
 * unapproved copy cannot reach a user unnoticed.
 *
 * **Why two dimensions.** The marker in an alt string records what was done to the image
 * (`kind`); it deliberately says nothing about which revision of the legal wording applies.
 * Those change independently: legal can reword all three kinds at once without any asset's
 * alt text changing, and a new kind would need copy in every existing version. Keying by
 * version *then* kind is what lets the control page offer "publish revision v2" as one act
 * rather than three, and what lets a page pin the version it rendered.
 *
 * A version is an opaque string, not a number, so it can carry whatever label legal
 * actually uses in review ("v2", "2026-q1"). Ordering is never inferred from it — there is
 * no "latest", only `DEFAULT_DISCLAIMER_VERSION`, because picking the newest automatically
 * is how unapproved copy would ship the moment someone drafted it.
 */
export const DISCLAIMERS: Record<string, Record<AiMarkerKind, string>> = {
  v1: {
    generated: "PENDING_LEGAL_This image was created using AI.",
    modified: "PENDING_LEGAL_This image was edited using AI.",
    unspecified: "PENDING_LEGAL_This image involved AI.",
  },
};

/**
 * The disclaimer for a marker kind, from a specific copy revision.
 *
 * An unrecognised `version` falls back to `DEFAULT_DISCLAIMER_VERSION` rather than throwing
 * or returning empty. The caller is usually a CMS field an editor typed into, so a typo or
 * a version deleted after a page pinned it must degrade to approved copy — rendering an
 * image with no disclaimer at all is the one outcome worse than rendering slightly stale
 * wording, because the disclaimer is the compliance artifact.
 */
export function getDisclaimer(
  kind: AiMarkerKind,
  version: string = DEFAULT_DISCLAIMER_VERSION,
): string {
  // Non-null: DEFAULT_DISCLAIMER_VERSION is a key of the literal above, so this cannot be
  // undefined. Only a future edit that removes it could break this.
  const fallback = DISCLAIMERS[DEFAULT_DISCLAIMER_VERSION]![kind];

  // `Object.hasOwn`, NOT `DISCLAIMERS[version] ?? fallback`. `DISCLAIMERS` is a plain
  // object, so a version naming an inherited property — `"__proto__"`, `"constructor"`,
  // `"toString"`, `"valueOf"`, `"hasOwnProperty"` — resolves through the prototype chain to
  // something TRUTHY, which defeats `??` entirely; the lookup then reads a missing key off
  // `Object.prototype` and yields `undefined`. That is the silent no-disclaimer-at-all
  // outcome this function exists to prevent, and `JSON.stringify` drops `undefined` values,
  // so it would reach a response as a simply-absent field rather than an error. The version
  // is editor-supplied free text from a CMS field, so this is reachable input, not exotica.
  if (!Object.hasOwn(DISCLAIMERS, version)) {
    return fallback;
  }

  // Belt to that brace: an own-property revision could still be missing this kind (a
  // version added at runtime without copy for every kind), so require a real string.
  const copy = DISCLAIMERS[version]?.[kind];

  return typeof copy === "string" && copy !== "" ? copy : fallback;
}
